import { useMeasure } from 'react-use';
import { RefCallback, useState } from 'react';

import { Duration } from 'utils/animateElement';

import { useInitialStylesOnMount } from './useInitialStylesOnMount';
import { useAnimatedStylesOnToggle } from './useAnimatedStylesOnToggle';

type UseCollapsibleHookResult<O, I> = {
  outerRef: RefCallback<O>;
  innerRef: RefCallback<I>;
};

/**
 * Hook that animates the collapse/expansion of `outerRef`'s height + opacity in order to hide/show `innerRef`
 */
export const useCollapsible = <O extends HTMLElement, I extends HTMLElement>(
  isCollapsed: boolean,
  animationDuration: Duration,
): UseCollapsibleHookResult<O, I> => {
  const [outerElement, outerRef] = useState<O | null>(null);
  const [innerRef, { height: innerHeight }] = useMeasure<I>();

  useInitialStylesOnMount(isCollapsed, outerElement);
  useAnimatedStylesOnToggle(isCollapsed, outerElement, innerHeight, animationDuration);

  return {
    outerRef,
    innerRef,
  };
};
